import React, { useState, useRef, useEffect } from 'react';
import { 
  Button, 
  Snackbar, 
  Alert, 
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import StopIcon from '@mui/icons-material/Stop';
import TimerIcon from '@mui/icons-material/Timer';
import axios from 'axios';

const StreamRecorder = ({ stream, PatientID, patientName, sampleID, onRecordingComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    // Cleanup timer on component unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Format recording time to minutes:seconds
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const showNotification = (message, severity = 'info') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setShowAlert(true);
  };

  const saveVideoToServer = async (blob) => {
    try {
      setIsSaving(true);
      const formData = new FormData();
      formData.append('video', blob, 'recording.webm');
      
      // Thêm thông tin bệnh nhân nếu có
      if (PatientID) formData.append('patient_id', PatientID);
      if (patientName) formData.append('patient_name', patientName);
      if (sampleID) formData.append('sample_id', sampleID);

      const response = await axios.post('https://sperms.ailab.vn:5005/save-video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      if (response.data.status === 'success') {
        showNotification('Video đã được lưu trên server', 'success');
        // Gọi callback để cập nhật danh sách video
        if (typeof onRecordingComplete === 'function') {
          onRecordingComplete();
        }
      } else {
        showNotification('Lưu video không thành công: ' + (response.data.message || 'Lỗi không xác định'), 'error');
      }
    } catch (error) {
      console.error('Error saving to server:', error);
      showNotification('Lỗi khi lưu video lên server: ' + error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const startRecording = async () => {
    if (!stream) {
      showNotification('Không có stream để record', 'error');
      return;
    }

    if (!PatientID || !patientName || !sampleID) {
      showNotification('Vui lòng nhập đầy đủ thông tin bệnh nhân và mẫu', 'warning');
      return;
    }

    try {
      const mimeTypes = [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm'
      ];
      
      let selectedMimeType = mimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || 'video/webm';
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType,
        videoBitsPerSecond: 2500000
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      // Reset and start the timer
      setRecordingTime(0);
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prevTime => prevTime + 1);
      }, 1000);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = (error) => {
        console.error('MediaRecorder error:', error);
        showNotification('Lỗi trong quá trình ghi video', 'error');
        stopRecording();
      };

      mediaRecorder.onstop = async () => {
        try {
          // Stop the timer
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }

          if (chunksRef.current.length === 0) {
            showNotification('Không có dữ liệu video để lưu', 'error');
            return;
          }

          const blob = new Blob(chunksRef.current, { type: selectedMimeType });
          
          if (blob.size === 0) {
            showNotification('File video rỗng', 'error');
            return;
          }

          // Lưu lên server
          showNotification('Đang lưu video lên server...', 'info');
          await saveVideoToServer(blob);
          
          chunksRef.current = [];
        } catch (error) {
          console.error('Error saving recording:', error);
          showNotification('Lỗi khi lưu file: ' + error.message, 'error');
        }
      };

      mediaRecorder.start(100);
      showNotification('Đang ghi video...', 'info');
      setIsRecording(true);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      showNotification('Không thể bắt đầu ghi video: ' + error.message, 'error');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        
        // Stop the timer
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      } catch (error) {
        console.error('Error stopping recording:', error);
        showNotification('Lỗi khi dừng ghi video', 'error');
      }
    }
  };

  return (
    <Box className="flex flex-col sm:flex-row items-center gap-2">
      <Button
        variant="contained"
        color={isRecording ? "error" : "primary"}
        onClick={isRecording ? stopRecording : startRecording}
        disabled={!stream || isSaving || !PatientID || !patientName || !sampleID}
        startIcon={
          isSaving ? <CircularProgress size={20} /> : 
          isRecording ? <StopIcon /> : <VideocamIcon />
        }
        className="w-full sm:w-auto min-w-[120px] whitespace-nowrap"
        size="medium"
      >
        {isSaving ? 'Đang lưu...' :
         isRecording ? 'Stop Record' : 'Record'}
      </Button>

      {isRecording && (
        <Box className="flex items-center gap-1 bg-red-100 px-3 py-1 rounded-md">
          <TimerIcon color="error" fontSize="small" />
          <Typography variant="body2" color="error" fontWeight="medium">
            {formatTime(recordingTime)}
          </Typography>
        </Box>
      )}

      <Snackbar 
        open={showAlert} 
        autoHideDuration={6000} 
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ 
          vertical: 'bottom', 
          horizontal: 'left' 
        }}
        className="max-w-[90vw] sm:max-w-[400px]"
      >
        <Alert 
          onClose={() => setShowAlert(false)} 
          severity={alertSeverity}
          className="w-full"
          variant="filled"
        >
          <span className="text-sm sm:text-base">
            {alertMessage}
          </span>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StreamRecorder;